import { useMemo } from "react";
import { useAppSelector } from "@app/hooks";
import { selectMCInternalExternalPresence } from "@store/Entities/ducks";
import { userRolesSelector, userIFCsSelector } from "@myProfile/store/selectors";
import { impersonationStateSelector } from "@impersonateUser/store/selectors";
import FeatureToggleService from "@services/FeatureToggleService";
import { ToggledFeature } from "@shared/models/featureToggle";

const USER_ROLE_NAMES = {
  PRIMARY_CONTACT: "Primary Contact",
  SECONDARY_CONTACT: "Secondary Contact",
} as const;

export interface ProfileMenuPermissions {
  /** true once the IFC fetch has settled (success or failure); false while loading. */
  permissionsLoaded: boolean;
  showMyProfile: boolean;
  showManageContacts: boolean;
  showPreferences: boolean;
  showImpersonate: boolean;
  isImpersonating: boolean;
}

/** Returns visibility flags for each profile-menu option based on the user's roles, IFCs, and fund types. */
export const useProfileMenuPermissions = (): ProfileMenuPermissions => {
  const { canImpersonate, isImpersonating } = useAppSelector(impersonationStateSelector);
  const { hasInternal, hasExternal } = useAppSelector(selectMCInternalExternalPresence);

  const userRolesState = useAppSelector(userRolesSelector);
  const userRoles = useMemo(() => userRolesState.data || [], [userRolesState.data]);

  const userIFCsState = useAppSelector(userIFCsSelector);

  const rolesLoaded = userRolesState.data !== null;
  const ifcsLoaded = userIFCsState.data !== null;

  const hasAnyIFCs = useMemo(() => {
    if (!ifcsLoaded) return false;

    return userIFCsState.data !== null && userIFCsState.data.length > 0;
  }, [userIFCsState.data, ifcsLoaded]);

  const isPrimaryContact = useMemo(() => {
    if (!rolesLoaded) return false;

    return userRoles.some((role) => role.roleName === USER_ROLE_NAMES.PRIMARY_CONTACT);
  }, [userRoles, rolesLoaded]);

  const isPrimaryOrSecondary = useMemo(() => {
    if (!rolesLoaded) return false;

    return userRoles.some(
      (role) => role.roleName === USER_ROLE_NAMES.PRIMARY_CONTACT || role.roleName === USER_ROLE_NAMES.SECONDARY_CONTACT
    );
  }, [userRoles, rolesLoaded]);

  const isMyProfileEnabled = FeatureToggleService.isFeatureEnabled(ToggledFeature.MyProfile);
  const showMyProfile = useMemo(() => isMyProfileEnabled && hasAnyIFCs, [isMyProfileEnabled, hasAnyIFCs]);

  const showManageContacts = useMemo(() => {
    if (!hasAnyIFCs) return false;
    if (hasExternal && isPrimaryOrSecondary) return true;
    if (hasInternal && !hasExternal && isPrimaryContact) return true;

    return false;
  }, [hasAnyIFCs, hasExternal, hasInternal, isPrimaryContact, isPrimaryOrSecondary]);

  const isManageContactsEnabled = FeatureToggleService.isFeatureEnabled(ToggledFeature.ManageContacts);

  // Settled once IFC data has resolved or errored; prevents a false-positive Access Denied
  // flash when the app loading spinner clears before the IFC API returns.
  const permissionsLoaded = ifcsLoaded || !!userIFCsState.error;

  return {
    permissionsLoaded,
    showMyProfile,
    showManageContacts: isManageContactsEnabled && showManageContacts,
    showPreferences: true,
    showImpersonate: canImpersonate,
    isImpersonating,
  };
};

export default useProfileMenuPermissions;
